/**
 * Derives one status card per payment method: Reimbursement or Pre-Paid, plus independent CourseCa$h™.
 * Actual reimbursement assistance overrides the template's pre-paid timing.
 * CourseCa$h™ requires an application election or an existing reserved/used balance, not template capability.
 * Requested non-card subtotals and approved reimbursement-only totals avoid counting card allocations twice.
 * Approval stage selects pre/final amounts; a final zero must not resurrect the pre-approved amount.
 * Reserved is the remaining card balance, already net of spending. Approved is used + raw reserved;
 * merchant-fee overspend can make reserved negative, so only the displayed remaining amount floors at zero.
 */

export type DisbursementCardType = 'reimbursement' | 'course-cash' | 'pre-paid';

export type DisbursementStatus =
  | 'requested'
  | 'pre-approved' // Reimbursement only — "course in progress"
  | 'pending' // Reimbursement + Pre-Paid — approved, not yet disbursed
  | 'paid' // Reimbursement + Pre-Paid
  | 'available' // CourseCa$h™ only — approved, reserved, not yet spent
  | 'active' // CourseCa$h™ only — spent at least once
  | 'deactivated'; // CourseCa$h™ only — card deactivated

export interface DisbursementPayment {
  /** Cents. */
  amount: number;
  /** ISO date of the payment; null while pending (nothing has settled yet). */
  date: string | null;
  /** Issued but not yet settled (assistance in PAYABLE). */
  pending: boolean;
}

export interface DisbursementCard {
  type: DisbursementCardType;
  status: DisbursementStatus;
  /** Cents. */
  requestedAmount: number;
  /** Cents — undefined at the `requested` status (nothing approved yet). */
  approvedAmount?: number;
  /** CourseCa$h™ only, `available`/`active` statuses. */
  useByDate?: string | null;
  /** CourseCa$h™ only, `active`/`deactivated` statuses. */
  usedAmount?: number;
  /** CourseCa$h™ only, `active` status. */
  remainingAmount?: number;
  /** Reimbursement/Pre-Paid payments, one row per issued payment; absent until a payment is issued. */
  payments?: DisbursementPayment[];
}

export interface DisbursementInput {
  state: string;
  /** Type 2/4.2 programs have no separate courses-approval step — their first
   * submission enters PENDING_EVIDENCE_APPROVAL directly (mirrors the private
   * firstTimeSubmissionMap in tas-application.ext.ts), so that state must be
   * treated as "requested", not "approved", for those two types only. */
  typeClassification: string;
  isPrepaidProgram: boolean;
  /** Any reimbursement assistance overrides the template's pre-paid timing. */
  hasReimbursementAssistance: boolean;
  /** Application payment election; template capability alone must not display CourseCa$h™. */
  isCourseCashElected: boolean;
  requestedTotal: number;
  /** Pre-approval CC$-eligible (tuition) subtotal — populated regardless of approval stage. */
  requestedCourseCashSubtotal: number;
  /** Pre-approval non-CC$ (books/labs/software/fees, or full total on a non-CC$ program) subtotal. */
  requestedNonCourseCashSubtotal: number;
  /** Reimbursement-only — see module doc. */
  preApprovedTotal: number;
  /** Reimbursement-only — see module doc. */
  finalApprovedTotal: number;
  /** Cents held by UNSETTLED reimbursement rows while every reimbursement is still UNSETTLED; an
   * approver's row edit lands here, not on the pre-approved field. Undefined otherwise. */
  currentReimbursementHold?: number;
  paidTotal: number;
  /** Current remaining CC$ balance (already net of used/refunded) — see module doc. */
  courseCashReserved: number;
  courseCashUsed: number;
  courseCashUseByDate?: string | null;
  /** Whether the application's active/primary PCS card has been deactivated. */
  pcsCardDeactivated: boolean;
  /** One row per SETTLED or PAYABLE reimbursement/pre-paid assistance. */
  payments?: DisbursementPayment[];
}

// Not yet approved — no approval amounts to show, whichever card.
const PRE_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
]);

// These two types have no courses-approval step — PENDING_EVIDENCE_APPROVAL
// is their first-submission state (tas-application.ext.ts's
// firstTimeSubmissionMap), so it means "just submitted", not "approved".
const EVIDENCE_APPROVAL_IS_FIRST_SUBMISSION = new Set(['TAS.ProgramType.2', 'TAS.ProgramType.4.2']);

// Evidence has actually been approved — only then can a reimbursement/pre-paid
// disbursement have moved past "course in progress" into pending/paid.
const POST_FINAL_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

export function deriveDisbursement(app: DisbursementInput): DisbursementCard[] {
  const cards: DisbursementCard[] = [];

  const isRequested =
    PRE_APPROVAL_STATES.has(app.state) ||
    (app.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL' &&
      EVIDENCE_APPROVAL_IS_FIRST_SUBMISSION.has(app.typeClassification));
  const isFinalApproved = POST_FINAL_APPROVAL_STATES.has(app.state);
  // Election first, issued-card money as the fallback — `used` alongside
  // `reserved` because a fully-spent card has reserved at 0 (module doc).
  const hasCourseCash =
    app.isCourseCashElected || app.courseCashReserved > 0 || app.courseCashUsed > 0;
  const nonCourseCashRequested = hasCourseCash
    ? app.requestedNonCourseCashSubtotal
    : app.requestedTotal;
  // Selected by approval STAGE, not truthiness — finalApprovedTotal can
  // legitimately be a real 0 once final approval reduces reimbursement to
  // zero, and `0 || preApprovedTotal` would resurrect the stale pre-approved
  // figure instead. Mirrors ManagePage's own finalApprovedLabel getter.
  const reimbursementApprovedAmount = isFinalApproved
    ? app.finalApprovedTotal
    : (app.currentReimbursementHold ?? app.preApprovedTotal);

  // Reimbursement / Pre-Paid — mutually exclusive by program payment timing.
  // (nonCourseCashRequested already equals requestedTotal whenever there's no
  // CourseCash portion, so this alone also correctly hides the card when
  // truly nothing has been requested at all.)
  if (nonCourseCashRequested > 0) {
    const primaryType: DisbursementCardType =
      app.isPrepaidProgram && !app.hasReimbursementAssistance ? 'pre-paid' : 'reimbursement';
    let status: DisbursementStatus;
    if (isRequested) {
      status = 'requested';
    } else if (app.paidTotal > 0) {
      // Checked before the approval-stage branch below: Pre-Paid assistance
      // settles on ATTEND, well before evidence approval, so gating "paid" on
      // isFinalApproved would leave a settled Pre-Paid card stuck on "pending".
      status = 'paid';
    } else if (primaryType === 'reimbursement' && !isFinalApproved) {
      // Pre-Paid has no separate "course in progress" chip in the Figma spec —
      // it goes straight to Pending once approved.
      status = 'pre-approved';
    } else {
      status = 'pending';
    }

    cards.push({
      type: primaryType,
      status,
      requestedAmount: nonCourseCashRequested,
      approvedAmount: isRequested ? undefined : reimbursementApprovedAmount,
      payments: app.payments?.length ? app.payments : undefined,
    });
  }

  // CourseCa$h™ — independent of the above; shows whenever this application
  // elected the card (or one is already funded), regardless of which
  // Reimbursement/Pre-Paid card (if any) also renders.
  if (hasCourseCash) {
    let status: DisbursementStatus;
    if (isRequested) {
      status = 'requested';
    } else if (app.pcsCardDeactivated) {
      status = 'deactivated';
    } else if (app.courseCashUsed > 0) {
      status = 'active';
    } else {
      status = 'available';
    }

    // reserved is the current remaining balance, not the original allocation —
    // add back what's already been used to get the original approved total.
    const courseCashApprovedAmount = app.courseCashUsed + app.courseCashReserved;

    cards.push({
      type: 'course-cash',
      status,
      requestedAmount: app.requestedCourseCashSubtotal,
      approvedAmount: isRequested ? undefined : courseCashApprovedAmount,
      useByDate:
        status === 'available' || status === 'active' ? app.courseCashUseByDate : undefined,
      usedAmount: status === 'active' || status === 'deactivated' ? app.courseCashUsed : undefined,
      // Merchant-fee overspend can make reserved negative; floor Remaining, but keep raw reserved when reconstructing Approved.
      remainingAmount: status === 'active' ? Math.max(0, app.courseCashReserved) : undefined,
    });
  }

  return cards;
}
